/**
 * App Component
 * 
 * Main application component with routing and layout.
 */

import { lazy, Suspense } from 'react';
import { lazyNamed } from './shared/utils/lazyRoute';
import { Spinner } from './shared/components/Spinner/Spinner';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import { AppLayout } from './shared/components/AppLayout';
import { TARIFF_OPTIMIZATION_ENABLED } from './config/featureFlags';
// #3452 — route-level code splitting. Every page below is its own chunk,
// fetched on first navigation; only the shell (layouts, auth, Home/Login/404)
// ships in the initial bundle. `v7_startTransition` keeps the current screen
// on view while the next chunk loads, so the fallback shows on cold start only.
import Home from './pages/Home';
import Login from './pages/Login';
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const EmployeeRegister = lazy(() => import('./pages/EmployeeRegister'));
const RoleSelection = lazy(() => import('./pages/RoleSelection'));
const ServiceProviderRegistration = lazy(() => import('./pages/ServiceProviderRegistration'));
const ServiceProviderRegister = lazy(() => import('./pages/ServiceProviderRegister'));
const ServiceProviderApprovalPending = lazy(() => import('./pages/ServiceProviderApprovalPending'));
const AdminServiceProviderApplications = lazy(() => import('./pages/AdminServiceProviderApplications'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminDirectory = lazy(() => import('./pages/AdminDirectory'));
const AdminUserDetail = lazy(() => import('./pages/AdminUserDetail'));
import { AdminLayout } from './shared/components/AdminLayout/AdminLayout';
const Optimizations = lazy(() => import('./pages/Optimizations'));
// import OptimizationsArchiveMobile from './pages/OptimizationsArchiveMobile'; // Deprecated - using drawer pattern now
const OptimizationDetailMobile = lazy(() => import('./pages/OptimizationDetailMobile'));
const TariffOptimizationMobile = lazy(() => import('./pages/TariffOptimizationMobile'));
const TariffListMobile = lazy(() => import('./pages/TariffListMobile'));
const TariffDetailMobile = lazy(() => import('./pages/TariffDetailMobile'));
const Devices = lazy(() => import('./pages/Devices'));
const RoomDetailPage = lazyNamed(() => import('./features/devices/components/RoomDetailPage'), 'RoomDetailPage');
const TelemetryDetailPage = lazyNamed(() => import('./features/devices/pages/TelemetryDetailPage'), 'TelemetryDetailPage');
const DiscoveryReviewPage = lazyNamed(() => import('./features/discovery'), 'DiscoveryReviewPage');
const CategoryManagementPage = lazyNamed(() => import('./features/discovery'), 'CategoryManagementPage');
const PropertySetupPage = lazyNamed(() => import('./features/property-setup'), 'PropertySetupPage');
const RoomsPage = lazyNamed(() => import('./features/rooms'), 'RoomsPage');
const Alarms = lazy(() => import('./pages/Alarms'));
const AlarmRules = lazy(() => import('./pages/AlarmRules'));
const AIAssistantMobilePage = lazyNamed(() => import('./features/ai-assistant'), 'AIAssistantMobilePage');
const AIAssistantChatPage = lazyNamed(() => import('./features/ai-assistant'), 'AIAssistantChatPage');
const ChatHistoryPage = lazyNamed(() => import('./features/ai-assistant'), 'ChatHistoryPage');
const AlarmAIAssistantPage = lazy(() => import('./pages/AlarmAIAssistant'));
const Calendar = lazy(() => import('./pages/Calendar'));
const CalendarCreateMobile = lazy(() => import('./pages/CalendarCreateMobile'));
const CalendarDayView = lazy(() => import('./pages/CalendarDayView'));
const AppointmentFormMobile = lazy(() => import('./pages/AppointmentFormMobile'));
const EventDetailMobile = lazy(() => import('./pages/EventDetailMobile'));
const OverdueAppointmentsMobile = lazy(() => import('./pages/OverdueAppointmentsMobile'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const Hub = lazy(() => import('./pages/Hub'));
const HubDocumentDetail = lazy(() => import('./pages/HubDocumentDetail'));
const Chat = lazy(() => import('./pages/Chat'));
const ChatMobile = lazy(() => import('./pages/ChatMobile'));
const ChatNewMobile = lazy(() => import('./pages/ChatNewMobile'));
const ChatArchive = lazy(() => import('./pages/ChatArchive'));
const Profile = lazyNamed(() => import('./features/profile'), 'Profile');
const ProfileEdit = lazyNamed(() => import('./features/profile/pages/ProfileEdit'), 'ProfileEdit');
const SubscriptionUpgrade = lazyNamed(() => import('./features/profile/pages/SubscriptionUpgrade'), 'SubscriptionUpgrade');
const PropertyCreate = lazyNamed(() => import('./features/properties/pages/PropertyCreate'), 'PropertyCreate');
const PropertyEditPage = lazyNamed(() => import('./features/properties/pages/PropertyEditPage'), 'PropertyEditPage');
const Documents = lazy(() => import('./pages/Documents'));
const MyDocuments = lazy(() => import('./pages/MyDocuments'));
const UploadDocument = lazy(() => import('./pages/UploadDocument'));
const CreateClaim = lazy(() => import('./pages/CreateClaim'));
const DocumentDetail = lazy(() => import('./pages/DocumentDetail'));
const DocumentPreviewMobile = lazy(() => import('./pages/DocumentPreviewMobile'));
const Objects = lazy(() => import('./pages/Objects'));
const Homeowner = lazy(() => import('./pages/Homeowner'));
const InviteServiceProviderMobile = lazy(() => import('./pages/InviteServiceProviderMobile'));
const ServiceProviderDetailMobile = lazy(() => import('./pages/ServiceProviderDetailMobile'));
const ServiceProviderEvents = lazy(() => import('./pages/ServiceProviderEvents'));
const ServiceProviderPage = lazy(() => import('./pages/ServiceProviderPage'));
const ServiceProviderDashboard = lazy(() => import('./pages/ServiceProviderDashboard'));
const HomeownerListPage = lazy(() => import('./pages/HomeownerListPage'));
const HomeownerDetailPage = lazy(() => import('./pages/HomeownerDetailPage'));
const MembersPage = lazyNamed(() => import('./features/homeownerMembers'), 'MembersPage');
const MemberPasswordSetup = lazy(() => import('./pages/MemberPasswordSetup'));
const PropertyProfilesPage = lazy(() => import('./pages/PropertyProfilesPage'));
const PresencePage = lazyNamed(() => import('./features/homeowner/components/PresencePage'), 'PresencePage');
const HomeOwnerPropertyProfileDetail = lazy(() => import('./pages/HomeOwnerPropertyProfileDetail.tsx'));
const ObjectDetail = lazy(() => import('./pages/ObjectDetail'));
const PropertiesList = lazy(() => import('./features/profile/pages/PropertiesList'));
const PropertyDetail = lazy(() => import('./features/profile/pages/PropertyDetail'));
const HomeownerDashboard = lazyNamed(() => import('./features/homeowner/pages/HomeownerDashboard'), 'HomeownerDashboard');
const EmployeeManagement = lazyNamed(() => import('./features/employees'), 'EmployeeManagement');
const TeamMemberDetail = lazyNamed(() => import('./features/employees/pages/TeamMemberDetail'), 'TeamMemberDetail');
const TeamMemberEdit = lazyNamed(() => import('./features/employees/pages/TeamMemberEdit'), 'TeamMemberEdit');
const TeamMemberInvite = lazyNamed(() => import('./features/employees/pages/TeamMemberInvite'), 'TeamMemberInvite');
const AccessControl = lazy(() => import('./pages/AccessControl'));
const CreateAccessCodePage = lazy(() => import('./pages/CreateAccessCode'));
const EditAccessCodePage = lazy(() => import('./pages/EditAccessCode'));
const FloorPlanView = lazy(() => import('./pages/FloorPlanView'));
const RoomBoundaryEditor = lazy(() => import('./pages/RoomBoundaryEditor'));
const RoomsView = lazy(() => import('./pages/RoomsView'));
const DevicesView = lazy(() => import('./pages/DevicesView'));
const ClusteringViewer = lazy(() => import('./pages/ClusteringViewer'));
const NetworksView = lazy(() => import('./pages/NetworksView'));
const NetworkDetailView = lazy(() => import('./pages/NetworkDetailView'));
const AccessCodeDetail = lazy(() => import('./pages/AccessCodeDetail'));
const AccessControlSPV = lazy(() => import('./pages/AccessControlSPV'));
import { Toaster } from 'sonner';
import { PWAUpdatePrompt } from './shared/components/PWAUpdatePrompt';
import './App.css';
import './styles/theme.css';
const AdminVendors = lazy(() => import('./pages/AdminVendors'));
const AdminCrawler = lazy(() => import('./pages/AdminCrawler'));
const AdminDiscovery = lazy(() => import('./pages/AdminDiscovery'));
const AdminHubDocuments = lazy(() => import('./pages/AdminHubDocuments'));
const IntegrationCallbackPage = lazyNamed(() => import('./features/integrations/pages/IntegrationCallbackPage'), 'IntegrationCallbackPage');
const DeviceDiscoveryPage = lazyNamed(() => import('./features/integrations/pages/DeviceDiscoveryPage'), 'DeviceDiscoveryPage');
const AllConnectorsPage = lazyNamed(() => import('./features/integrations/pages/AllConnectorsPage'), 'AllConnectorsPage');
const SyncConflictsPage = lazyNamed(() => import('./features/integrations/pages/SyncConflictsPage'), 'SyncConflictsPage');
const IntegrationMonitoringPage = lazyNamed(() => import('./features/integrations/pages/IntegrationMonitoringPage'), 'IntegrationMonitoringPage');
const GroheDevicesPage = lazyNamed(() => import('./features/integrations/pages/GroheDevicesPage'), 'GroheDevicesPage');
const GroheSettingsPage = lazyNamed(() => import('./features/integrations/pages/GroheSettingsPage'), 'GroheSettingsPage');
const TuyaDevicesPage = lazyNamed(() => import('./features/integrations/pages/TuyaDevicesPage'), 'TuyaDevicesPage');
const WishlistPage = lazyNamed(() => import('./features/wishlist/pages/WishlistPage'), 'WishlistPage');
// #2886 Phase D — ZCI detail page replaces the old health-score detail (v1 under
// CEO review). The old page import is kept for a one-line revert if needed:
// import { HealthScoreDetailPage } from './features/healthpass/pages/HealthScoreDetailPage';
const ZciDetailPage = lazyNamed(() => import('./features/healthpass/pages/ZciDetailPage'), 'ZciDetailPage');
const WishlistDetailMobile = lazy(() => import('./pages/WishlistDetailMobile'));
const AdminSimulator = lazy(() => import('./pages/AdminSimulator'));
const AdminPatternPreview = lazy(() => import('./pages/AdminPatternPreview'));
const ModelCompare = lazy(() => import('./pages/ModelCompare'));
// Dev-only primitive preview harness (#2500). Lazy + DEV-gated so it is
// never bundled into production builds. Direct URL only: /dev/primitives.
const PrimitivesPreview = lazy(() =>
  import('./features/dashboard/components/primitives/__preview__/PrimitivesPreview').then(
    (m) => ({ default: m.PrimitivesPreview }),
  ),
);
// Dev-only WKWebView sandbox jail spike (Track B day-1 GO/NO-GO gate). Lazy +
// DEV-gated so it is never bundled into production. Direct URL only:
// /dev/wkwebview-spike. Not user-facing, never linked from a menu.
const WkwebviewSandboxSpike = lazy(() =>
  import('./dev/wkwebviewSandboxSpike').then((m) => ({ default: m.WkwebviewSandboxSpike })),
);
const AdminNetworkDevices = lazy(() => import('./pages/AdminNetworkDevices'));
const AdminNetworkDeviceDetail = lazy(() => import('./pages/AdminNetworkDeviceDetail'));
import NotFound from './pages/NotFound';
import { ProtectedRoute } from './services/auth/AuthProvider';
import { OfflineIndicator } from './shared/components/OfflineIndicator';

/**
 * Intercepts OAuth callback params (?code=&state=) at the root URL
 * and forwards them to the integration callback page.
 * Needed when a provider's registered callback URL is the frontend origin.
 */
function OAuthRedirectHandler() {
  const [searchParams] = useSearchParams();
  const location = window.location;

  // Only intercept OAuth params at the root path — NOT on /integrations/callback
  // (otherwise it creates an infinite redirect loop)
  if (location.pathname !== '/' && location.pathname !== '') {
    return null;
  }

  const code = searchParams.get('code');
  const state = searchParams.get('state');

  if (code && state) {
    const callbackUrl = `/integrations/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    window.location.replace(callbackUrl);
    return <div>Redirecting...</div>;
  }

  return null;
}

/** Shown while a route chunk downloads on cold start (#3452). */
function RouteFallback() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner size="lg" />
    </div>
  );
}

function App() {
  return (
    <>
      <OfflineIndicator />
      <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#E0DFDA',
            color: '#262626',
            border: '1px solid #CACAC2',
            fontFamily: 'Archivo, sans-serif',
          },
          className: 'custom-toast',
        }}
      />
      <PWAUpdatePrompt />
      <OAuthRedirectHandler />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/role" element={<RoleSelection />} />
        <Route path="/service-provider/register" element={<ServiceProviderRegister />} />
        <Route path="/service-provider/application" element={<ServiceProviderRegistration />} />
        <Route path="/service-provider/approval-pending" element={<ServiceProviderApprovalPending />} />
        <Route path="/employee/register" element={<EmployeeRegister />} />
        <Route path="/member/setup-password" element={<MemberPasswordSetup />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/integrations/callback" element={<IntegrationCallbackPage />} />
        <Route
          path="/integrations/discovery"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <DeviceDiscoveryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/all"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <AllConnectorsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/grohe/devices"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <GroheDevicesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/grohe/settings"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <GroheSettingsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/tuya/devices"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <TuyaDevicesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sp-applications"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminServiceProviderApplications />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/directory"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminDirectory />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminUserDetail />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/vendors"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminVendors />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/crawler"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminCrawler />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/discovery"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminDiscovery />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/hub-documents"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminHubDocuments />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/integrations/conflicts"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <SyncConflictsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/integrations/monitoring"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <IntegrationMonitoringPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/simulator"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminSimulator />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        {/* Widget composition v2 (#2497) — private staging review.
            Not linked from menu. Direct URL only.
            Renders all 3 P2 patterns side-by-side for CEO sign-off
            BEFORE flipping ENABLE_WIDGET_PATTERNS_V2 for users. */}
        <Route
          path="/admin/patterns"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminPatternPreview />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        {/* Model-comparison eval screen (#2842) — talks to the LOCAL
            model-eval harness (port 8010), never chat-api. Lives in the
            general app so the property selector scopes the run. Not linked
            from menu. Direct URL only. */}
        <Route
          path="/model-compare"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ModelCompare />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Dev-only primitive preview (#2500). Mounted only in dev builds. */}
        {import.meta.env.DEV && (
          <Route
            path="/dev/primitives"
            element={
              <Suspense fallback={null}>
                <PrimitivesPreview />
              </Suspense>
            }
          />
        )}
        {/* Dev-only WKWebView sandbox jail spike (Track B day-1 gate). Dev builds only. */}
        {import.meta.env.DEV && (
          <Route
            path="/dev/wkwebview-spike"
            element={
              <Suspense fallback={null}>
                <WkwebviewSandboxSpike />
              </Suspense>
            }
          />
        )}
        <Route
          path="/admin/network-devices"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminNetworkDevices />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/network-devices/:vendorName"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout>
                <AdminNetworkDeviceDetail />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/dashboard"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <RoleAwareDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/presence"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <PresencePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/property-profiles/:objectId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <PropertyProfilesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Devices />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/room/:roomId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <RoomDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/telemetry/:deviceId/:telemetryKey"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <TelemetryDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/discovery"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <DiscoveryReviewPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/categories"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <CategoryManagementPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/rooms"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <RoomsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/floor-plan/:objectId/:floorPlanId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <FloorPlanView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/floor-plan/:objectId/:floorPlanId/edit-rooms"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <RoomBoundaryEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rooms/:objectId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <RoomsView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/:objectId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <DevicesView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clustering/:deviceId"
          element={<ClusteringViewer />}
        />
        <Route
          path="/network"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <NetworksView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/network/:id"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <NetworkDetailView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/optimizations/:id"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <OptimizationDetailMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/optimizations"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Optimizations />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* #2882 — tariff optimiser deactivated (not deleted); deep links fall
            through to the catch-all while the flag is off. */}
        {TARIFF_OPTIMIZATION_ENABLED && (
          <>
            <Route
              path="/tariff-optimization"
              element={
                <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
                  <AppLayout>
                    <TariffOptimizationMobile />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tariff-list"
              element={
                <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
                  <AppLayout>
                    <TariffListMobile />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tariff/:id"
              element={
                <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
                  <AppLayout>
                    <TariffDetailMobile />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
          </>
        )}
        <Route
          path="/wishlist"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <WishlistPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/wishlist/:id"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <WishlistDetailMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/alarms"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Alarms />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/alarms/:alarmId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Alarms />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/alarms/ai-assistant"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <AlarmAIAssistantPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/alarms/rules"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <AlarmRules />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/access-control"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <AppLayout>
                <AccessControl />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/access-control/create"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <CreateAccessCodePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/access-control/edit/:id"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <EditAccessCodePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/access-control-spv"
          element={
            <ProtectedRoute forbidRoles={['admin', 'viewer']} redirectTo="/404" show404={true}>
              <AppLayout>
                <AccessControlSPV />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/access-control/:id"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/404" show404={true}>
              <AccessCodeDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-assistant/:conversationId?"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <AIAssistantMobilePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-assistant/history"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <ChatHistoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-assistant/chat"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <AIAssistantChatPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Calendar />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar/day/:date"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <CalendarDayView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar/create"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <CalendarCreateMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar/event/:id"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <EventDetailMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar/overdue"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <OverdueAppointmentsMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar/edit/:id"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <AppointmentFormMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/homeowner/events"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <ServiceProviderEvents />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/404" show404={true}>
              <AppLayout>
                <TasksPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/zentri-hub"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Hub />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/zentri-hub/:docId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <HubDocumentDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Chat />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/new"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <ChatNewMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/archive"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <ChatArchive />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:conversationId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <ChatMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Profile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute forbidRoles={['admin', 'viewer']} redirectTo="/404" show404={true}>
              <AppLayout>
                <Documents />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-documents"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <AppLayout>
                <MyDocuments />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/upload-document"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <AppLayout>
                <UploadDocument />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-claim"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <AppLayout>
                <CreateClaim />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/document-detail/:id"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <AppLayout>
                <DocumentDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents/preview/:id"
          element={
            <ProtectedRoute forbidRoles={['admin', 'viewer']} redirectTo="/404" show404={true}>
              <DocumentPreviewMobile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/property-profiles"
          element={
            <ProtectedRoute forbidRoles={['admin', 'viewer']} redirectTo="/404" show404={true}>
              <AppLayout>
                <Objects />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-owner/property-profiles"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/404" show404={true}>
              <AppLayout>
                <PropertiesList />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-owner/property-profiles/:objectId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/404" show404={true}>
              <AppLayout>
                <HomeOwnerPropertyProfileDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-owner/property/:propertyId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/404" show404={true}>
              <AppLayout>
                <PropertyDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-owner/profile"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Profile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-owner/profile/edit"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <ProfileEdit />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-owner/profile/subscription"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <SubscriptionUpgrade />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-owner/property/create"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <PropertyCreate />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-owner/property/edit/:propertyId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <PropertyEditPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/objects/:objectId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <ObjectDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee-management"
          element={
            <ProtectedRoute forbidRoles={['admin', 'viewer']} redirectTo="/404" show404={true}>
              <AppLayout>
                <EmployeeManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/team"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <EmployeeManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/invite"
          element={
            <ProtectedRoute requiredRole="technician">
              <TeamMemberInvite />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:id/edit"
          element={
            <ProtectedRoute requiredRole="technician">
              <TeamMemberEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:id"
          element={
            <ProtectedRoute requiredRole="technician">
              <TeamMemberDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/homeowner"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <Homeowner />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/homeowner/invite"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <AppLayout>
                <InviteServiceProviderMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/homeowner/:providerId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <ServiceProviderDetailMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/health-score/:objectId"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <AppLayout>
                {/* #2886 Phase D: ZCI 5-pillar view. Revert to <HealthScoreDetailPage /> if pulled. */}
                <ZciDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/service-provider/dashboard">
              <AppLayout>
                <HomeownerDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/homeowner/members"
          element={
            <ProtectedRoute forbidRoles={['admin', 'technician']} redirectTo="/404" show404={true}>
              <AppLayout>
                <MembersPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <AppLayout>
                <ServiceProviderPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/dashboard"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <ServiceProviderDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/customers"
          element={
            <ProtectedRoute forbidRoles={['admin', 'viewer']} redirectTo="/404" show404={true}>
              <AppLayout>
                <HomeownerListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/customers/:customerId"
          element={
            <ProtectedRoute forbidRoles={['admin', 'viewer']} redirectTo="/404" show404={true}>
              <AppLayout>
                <HomeownerDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/documents/:id"
          element={
            <ProtectedRoute forbidRoles={['admin', 'viewer']} redirectTo="/404" show404={true}>
              <AppLayout>
                <DocumentDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* ========== SERVICE PROVIDER ROUTE ALIASES ========== */}
        {/* Same components as homeowner, under /service-provider/* prefix */}
        <Route
          path="/service-provider/alarms"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <Alarms />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/alarms/:alarmId"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <Alarms />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/alarms/ai-assistant"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <AlarmAIAssistantPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/alarms/rules"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <AlarmRules />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/devices"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <Devices />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/devices/room/:roomId"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <RoomDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/devices/telemetry/:deviceId/:telemetryKey"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <TelemetryDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/optimizations"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <Optimizations />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/ai-assistant/:conversationId?"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <AIAssistantMobilePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/ai-assistant/history"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <ChatHistoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/ai-assistant/chat"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <AIAssistantChatPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/calendar"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <Calendar />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/calendar/day/:date"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <CalendarDayView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/calendar/create"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <CalendarCreateMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/calendar/event/:id"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <EventDetailMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/calendar/edit/:id"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <AppointmentFormMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/chat"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <Chat />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/chat/new"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <ChatNewMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/chat/:conversationId"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <ChatMobile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/profile"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <Profile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/documents"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <Documents />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/zentri-hub"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <Hub />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/zentri-hub/:docId"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <HubDocumentDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/property-profiles"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <PropertiesList />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/access-control"
          element={
            <ProtectedRoute requiredRole="technician">
              <AppLayout>
                <AccessControlSPV />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Property Setup - DEDICATED FULL-PAGE, NO AppLayout wrapper (USER LOCKED DECISION - NOT a modal) */}
        <Route
          path="/property-setup/:propertyId"
          element={
            <ProtectedRoute forbidRoles={['admin']} redirectTo="/admin">
              <PropertySetupPage />
            </ProtectedRoute>
          }
        />

        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
    </>
  );
}

export default App;



